import { useMemo, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { isAfter, startOfDay, addDays } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { isEqual } from 'lodash'
import {
  GetGiveawayScheduleQuery,
  GetGiveawayScheduleQueryVariables,
  SweepstakesEntriesTypes,
  SweepstakesScheduleItemObject,
} from '@/types/codegen-federation'

const GET_GIVEAWAY_SCHEDULE = gql`
  query getGiveawaySchedule($input: TicketSalesPromosInput!) {
    ticketSalesPromos(input: $input) {
      sweepstakesSchedule {
        isActive
        name
        endDate
        startDate
        dailyBonusEntries
        minRewardTicketQuantity
        items {
          ticketType
          multiplier
          entriesPerTicket
          startDate
          endDate
          itemType
          quantityMax
          quantityMin
        }
      }
    }
  }
`

export const useGiveawaySchedule = (theatricalSlug: string | undefined) => {
  const [isReady, setIsReady] = useState(false)
  const { data, refetch } = useQuery<GetGiveawayScheduleQuery, GetGiveawayScheduleQueryVariables>(
    GET_GIVEAWAY_SCHEDULE,
    {
      variables: { input: { projectSlug: theatricalSlug === 'rule-breakers-tech' ? 'rule-breakers' : theatricalSlug } },
      onCompleted: () => setIsReady(true),
    },
  )

  return useMemo(() => {
    const giveawaySchedule = data?.ticketSalesPromos?.sweepstakesSchedule?.[0]
    const schedules = giveawaySchedule?.items

    return {
      isReady,
      isActive: giveawaySchedule?.endDate ? isGiveawayActive(giveawaySchedule.endDate) : false,
      presale: findSchedule(schedules, 'PRESALE'),
      normal: findSchedule(schedules, 'NORMAL'),
      guildSubscription: findSchedule(schedules, 'GUILD_SIGNUP_MONTHLY'),
      guildTicketRedemption: findSchedule(schedules, 'GUILD_TICKET'),
      dailyBonusEntries: giveawaySchedule?.dailyBonusEntries ?? 0,
      minRewardTicketQuantity: giveawaySchedule?.minRewardTicketQuantity ?? 0,
      quantityMultipliers: getQuantityMultipliers(schedules),
      refetch,
    }
  }, [data?.ticketSalesPromos?.sweepstakesSchedule, isReady, refetch])
}

const findSchedule = (schedules: SweepstakesScheduleItemObject[] | null | undefined, type: SweepstakesEntriesTypes) => {
  if (!schedules) return null
  if (type === 'NORMAL') {
    return schedules.find((schedule) => schedule.ticketType === 'NORMAL' && schedule.itemType === 'TEMPORAL_ONLY')
  }

  return schedules.find((schedule) => schedule.ticketType === type)
}

const getQuantityMultipliers = (schedules: SweepstakesScheduleItemObject[] | null | undefined) => {
  if (!schedules) return null

  const quantitySets = schedules.filter((schedule) => schedule.itemType === 'QUANTITY')

  if (!quantitySets || quantitySets.length === 0) return null

  return quantitySets.map((set) => {
    return {
      min: set?.quantityMin ?? 1,
      max: set?.quantityMax ?? Infinity,
      multiplier: set.multiplier,
    }
  })
}

export const isGiveawayActive = (endDate: Date) => {
  const timeZone = 'America/Denver'
  const parsedEndDate = toZonedTime(new Date(addDays(endDate, 2)), timeZone)
  const startOfEndDate = startOfDay(parsedEndDate)
  const startOfToday = startOfDay(toZonedTime(new Date(), timeZone))
  return isEqual(startOfEndDate, startOfToday) || isAfter(startOfEndDate, startOfToday)
}
